/**
 * LEG-UP registration validation helpers (frontend format checks only).
 * Country-specific ID and VAT syntax checks do not verify authenticity,
 * official registry status, checksum, or the user's identity.
 * Mirror essential rules in the Django/DRF serializers.
 */
const asText = (v) => String(v ?? "");
const trimmed = (v) => asText(v).trim();

export const isValidPersonName = (value) => {
  const name = trimmed(value);
  return name.length <= 120 &&
    /^[\p{L}\p{M}][\p{L}\p{M}\s.'’\-]*$/u.test(name);
};

export const isValidEmail = (value) => {
  const email = trimmed(value);
  if (!email || email.length > 254 || /\s/.test(email)) return false;
  const parts = email.split("@");
  if (parts.length !== 2 || parts[0].length > 64 || !parts[0]) return false;
  const domain = parts[1];
  if (!/^[A-Za-z0-9.-]+$/.test(domain) || !domain.includes(".")) return false;
  const labels = domain.split(".");
  if (labels.some((part) => !part || part.length > 63 || !/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(part))) return false;
  return /^[^\s@]+$/.test(parts[0]);
};

/** HB phone input has a separate country dial-code prefix, so store local digits. */
export function normalizeLocalPhone(value, country = "", dialCode = "") {
  const input = trimmed(value);
  let digits = input.replace(/\D/g, "");
  const prefix = asText(dialCode).replace(/\D/g, "");
  // Recognize +CC, 00CC, and clear country-prefixed invite values.
  const maxNationalDigits = {BE: 10, ES: 9, GR: 10, LU: 11}[country] || 14;
  if (prefix && (input.startsWith("+") || input.startsWith("00") || digits.length > maxNationalDigits)) {
    const bare = digits.startsWith("00") ? digits.slice(2) : digits;
    if (bare.startsWith(prefix)) {
      digits = bare.slice(prefix.length);
      // BE international phone numbers omit the domestic trunk prefix 0.
      if (country === "BE" && digits && !digits.startsWith("0")) digits = `0${digits}`;
    }
  }
  // No unconditional slicing: an invalid number must be flagged, not truncated.
  return digits;
}

export function isValidLocalPhone(value, country) {
  const digits = trimmed(value);
  if (!/^\d+$/.test(digits)) return false;
  switch (country) {
    case "BE": return /^0[1-9]\d{7,8}$/.test(digits); // 9-digit landline / 10-digit mobile
    case "ES": return /^[6789]\d{8}$/.test(digits);   // 9 digits
    case "GR": return /^[26]\d{9}$/.test(digits);     // 10 digits (fixed/mobile)
    case "LU": return /^\d{4,11}$/.test(digits);       // variable-length plans
    default: return /^\d{6,14}$/.test(digits);          // remaining EU countries
  }
}

export function normalizeNationalId(value, country) {
  const raw = trimmed(value).toUpperCase();
  if (country === "BE" || country === "LU") return raw.replace(/[.\s/-]/g, "");
  return raw;
}

export function isValidNationalId(value, country) {
  const text = normalizeNationalId(value, country);
  if (!text) return true; // Optional in the current HB registration workflow.
  switch (country) {
    case "BE": return /^\d{11}$/.test(text);
    case "ES": return /^(?:\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/.test(text);
    case "GR": return /^(?:[A-ZΑ-Ω]{1,2}\d{6,8}|\d{6,12})$/u.test(text);
    case "LU": return /^\d{13}$/.test(text);
    default: return text.length >= 5 && text.length <= 40;
  }
}

export function normalizeVat(value, country) {
  const v = trimmed(value).toUpperCase().replace(/[\s.-]/g, "");
  if (!v) return "";
  if (country === "BE" && /^\d{10}$/.test(v)) return `BE${v}`;
  if (country === "ES" && /^(?:\d{8}[A-Z]|[A-Z]\d{7}[A-Z0-9])$/.test(v)) return `ES${v}`;
  return v;
}

export function isValidVat(value, country) {
  const vat = normalizeVat(value, country);
  if (!vat) return false;
  if (country === "BE") return /^BE[01]\d{9}$/.test(vat);
  if (country === "ES") return /^ES(?:\d{8}[A-Z]|[A-Z]\d{7}[A-Z0-9])$/.test(vat);
  // No invented detailed VAT rules for other EU countries.
  // Generic syntax only; registry verification belongs to the backend.
  return /^[A-Z0-9]{4,30}$/.test(vat);
}

export function validateLegalDocument(file, {maxMegabytes = 20} = {}) {
  if (!file) return "validation.notaryRequired";
  if (!(typeof File !== "undefined" && file instanceof File)) return "validation.notaryRequired";
  const mimeTypes = ["application/pdf", "image/jpeg", "image/png"];
  const extensions = /\.(?:pdf|png|jpe?g)$/i;
  if (!mimeTypes.includes(file.type) || !extensions.test(file.name)) {
    return "validation.notaryFileType";
  }
  if (file.size <= 0 || file.size > maxMegabytes * 1024 * 1024) {
    return "validation.notaryFileSize";
  }
  return "";
}

const err = (errors, field, key) => { if (key) errors[field] = key; };

/** Returns translation keys. Components translate them at display time. */
export function getHomebuyerRegistrationErrors(form, isStrongPassword) {
  const errors = {};
  if (!trimmed(form.first_name)) err(errors, "first_name", "validation.firstNameRequired");
  else if (!isValidPersonName(form.first_name)) err(errors, "first_name", "validation.firstNameLettersOnly");
  if (!trimmed(form.last_name)) err(errors, "last_name", "validation.lastNameRequired");
  else if (!isValidPersonName(form.last_name)) err(errors, "last_name", "validation.lastNameLettersOnly");
  if (!trimmed(form.country_of_residence)) err(errors, "country_of_residence", "validation.countryResidenceRequired");
  if (!trimmed(form.country_of_property_purchase)) err(errors, "country_of_property_purchase", "validation.countryPurchaseRequired");
  if (!trimmed(form.email)) err(errors, "email", "validation.emailRequired");
  else if (!isValidEmail(form.email)) err(errors, "email", "validation.emailInvalid");
  if (!trimmed(form.confirm_email)) err(errors, "confirm_email", "validation.confirmEmailRequired");
  else if (!isValidEmail(form.confirm_email)) err(errors, "confirm_email", "validation.emailInvalid");
  else if (trimmed(form.email).toLowerCase() !== trimmed(form.confirm_email).toLowerCase()) {
    err(errors, "confirm_email", "validation.emailsDoNotMatch");
  }
  if (!form.password) err(errors, "password", "validation.passwordRequired");
  else if (!isStrongPassword(form.password)) err(errors, "password", "validation.passwordWeak");
  if (!form.confirm_password) err(errors, "confirm_password", "validation.confirmPasswordRequired");
  else if (form.password !== form.confirm_password) err(errors, "confirm_password", "validation.passwordsDoNotMatch");
  if (trimmed(form.national_identification_number) &&
      !isValidNationalId(form.national_identification_number, form.country_of_residence)) {
    const c = form.country_of_residence;
    err(errors, "national_identification_number", c === "GR" ? "validation.greekIdInvalid" :
      c === "BE" ? "validation.belgianIdInvalid" : c === "ES" ? "validation.spanishIdInvalid" :
      c === "LU" ? "validation.luxIdInvalid" : "validation.nationalIdInvalid");
  }
  if (!trimmed(form.phone)) err(errors, "phone", "validation.phoneRequired");
  else if (!isValidLocalPhone(form.phone, form.country_of_residence)) {
    const c = form.country_of_residence;
    err(errors, "phone", c === "BE" ? "validation.phoneInvalidBE" :
      c === "ES" ? "validation.phoneInvalidES" : c === "GR" ? "validation.phoneInvalidGR" :
      c === "LU" ? "validation.phoneInvalidLU" : "validation.phoneInvalid");
  }
  if (!form.agree_terms) err(errors, "agree_terms", "validation.agreeTermsRequired");
  return errors;
}

export function getLenderRegistrationErrors(form, isStrongPassword) {
  const errors = {};
  const nameFields = ["first_name", "last_name"];
  for (const field of nameFields) {
    if (!trimmed(form[field])) err(errors, field, `validation.${field === "first_name" ? "firstNameRequired" : "lastNameRequired"}`);
    else if (!isValidPersonName(form[field])) err(errors, field, "validation.nameInvalid");
  }
  if (!trimmed(form.role_in_organisation)) err(errors, "role_in_organisation", "validation.roleRequired");
  if (!trimmed(form.email)) err(errors, "email", "validation.emailRequired");
  else if (!isValidEmail(form.email)) err(errors, "email", "validation.emailInvalid");
  if (!trimmed(form.confirm_email)) err(errors, "confirm_email", "validation.confirmEmailRequired");
  else if (!isValidEmail(form.confirm_email)) err(errors, "confirm_email", "validation.emailInvalid");
  else if (trimmed(form.email).toLowerCase() !== trimmed(form.confirm_email).toLowerCase()) {
    err(errors, "confirm_email", "validation.emailsDoNotMatch");
  }
  if (!form.password) err(errors, "password", "validation.passwordRequired");
  else if (!isStrongPassword(form.password)) err(errors, "password", "validation.passwordWeak");
  if (!form.confirm_password) err(errors, "confirm_password", "validation.confirmPasswordRequired");
  else if (form.password !== form.confirm_password) err(errors, "confirm_password", "validation.passwordsDoNotMatch");
  if (form.registration_mode === "CREATE_ORGANISATION") {
    if (!trimmed(form.country)) err(errors, "country", "validation.countryRequired");
    if (!trimmed(form.organisation_name)) err(errors, "organisation_name", "validation.organisationNameRequired");
    else if (trimmed(form.organisation_name).length > 200 || /[\x00-\x1f\x7f]/.test(form.organisation_name)) {
      err(errors, "organisation_name", "validation.organisationNameInvalid");
    }
    if (!trimmed(form.vat)) err(errors, "vat", "validation.vatRequired");
    else if (!isValidVat(form.vat, form.country)) {
      err(errors, "vat", form.country === "BE" ? "validation.vatInvalidBE" :
        form.country === "ES" ? "validation.vatInvalidES" : "validation.vatInvalid");
    }
    err(errors, "notary_document", validateLegalDocument(form.notary_document));
  } else if (form.registration_mode === "JOIN_WITH_KEY") {
    if (!trimmed(form.company_key)) err(errors, "company_key", "validation.companyKeyRequired");
    else if (trimmed(form.company_key).length > 128 || /\s/.test(trimmed(form.company_key))) {
      err(errors, "company_key", "validation.companyKeyInvalid");
    }
  }
  if (!form.agree_terms) err(errors, "agree_terms", "validation.agreeTermsRequired");
  return errors;
}
